import { cutApi, CutAPI, CutType } from '@api/cut';
import dayjs from 'dayjs';
import { Box, Grid, Link, Menu, MenuItem, Typography, useMediaQuery, useTheme, Pagination } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckIcon from '@mui/icons-material/Check';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import { ScrollToTop } from '@components/ScrollToTop';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useStyles } from './styles/CutActivity.styles';
import { useSelector } from 'react-redux';
import { SortDirections, SortField } from '@/types/sort';
import { dateFormat, getMonthInfo } from '@utils/dates';
import { getFilterParams, cascadeOptions, isFilterSelected } from '@utils/filters';
import { differenceObjects, transformCuts } from '@utils/data';
import { parseActivityParams } from '@utils/activityLinks';
import { CutFilterOptions } from '@/types/cuts';
import { useFetch } from '@hooks/useFetch';
import { useOrgs } from '@hooks/useOrgs';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useScrollablePagination } from '@hooks/useScrollablePagination';
import { CUT_ACTIVITY_COLUMNS, SearchIDType, TYPES } from '@consts/cuts';
import { useAlert } from '@hooks/useAlert';
import { MultiSelectFilter } from '@components/MultiSelectFilter';
import { CustomDatePicker } from '@components/CustomDatePicker';
import { StationSearchFilter } from '@components/StationSearchFilter';
import { MobileCutCard, MobileCutCardSkeleton } from '@components/MobileCutCard';

import type { Cut, CutFilter, ParsedCut } from '@/types/cuts';
import type { RootState } from '@features/redux/store';
import { AppRoutes } from '@consts/routes';
import { getDeviceType, openPopup } from '@utils/index';
import type { ColumnOptions } from '@components/tables/DataTable';
import { useSearchParams } from 'react-router-dom';
import { CutDateUpload } from '@components/CutDateUpload';
import { OrgUrls, fetchOrgGeneric } from '@api/org';
import { REPORT } from '@consts/reports';
import { fetchReport } from '@api/report';
import type { ProductName } from '@/types/products';

// Design System Components
import {
    colors,
    ContentPageHeader,
    FilterPanel,
    FilterSection,
    FilterChip,
    Button,
    SelectField,
    TextField,
    TableCard,
    TableInfoHeader,
    DataTable as DesignDataTable,
    StatusBadge,
    TableLink,
} from '@components/design-system';
import type { DataTableColumn } from '@components/design-system/components/Table/DataTable';
import type { GridSortModel } from '@mui/x-data-grid-pro';

// Use prod if not running local dev
const UPDATES_URL = import.meta.env.VITE_MO_UPDATES_URI;

const PAGE_SIZE = 50;
const DATE_LIMIT = 12; // in months

type ActivityParamInput = {
    dateRange: { startDate: string; endDate: string };
    filters: Parameters<typeof getFilterParams>[0];
    searchId: string;
    searchType: SearchIDType;
    entryCode: string;
    employeeEmail: string;
    sortDirection: SortDirections;
    sortField: SortField;
    pageNum: number;
};

/**
 * Single source of truth for the cut-activity request. Everything goes through
 * /activity so org, product, type and pagination always compose. An id or
 * entry-code search is selective, so it runs all-time (no date constraint);
 * EntryCode maps to the entryCode predicate since it isn't a batch/credit id.
 */
const buildActivityParams = ({
    dateRange,
    filters,
    searchId,
    searchType,
    entryCode,
    employeeEmail,
    sortDirection,
    sortField,
    pageNum,
}: ActivityParamInput) => {
    const isEntryCodeSearch = Boolean(searchId) && searchType === SearchIDType.EntryCode;
    const isIdSearch = Boolean(searchId) && searchType !== SearchIDType.EntryCode;

    return {
        ...(searchId ? {} : dateRange),
        ...getFilterParams(filters),
        ...(isIdSearch ? { searchID: searchId, searchIDType: searchType } : {}),
        entryCode: (isEntryCodeSearch ? searchId : entryCode) || undefined,
        employeeEmail: employeeEmail || undefined,
        sortDirection,
        sortField,
        pageNum: Number.isNaN(pageNum) ? 0 : pageNum,
        pageSize: PAGE_SIZE,
    };
};

const DEFAULT_COLUMN_FILTER_STATE = CUT_ACTIVITY_COLUMNS.reduce((acc, { field }) => {
    acc[field] = true;
    return acc;
}, {} as Record<string, boolean>);

const DEFAULT_COLUMN_ORDER = CUT_ACTIVITY_COLUMNS.map(({ field }) => field as string);

const INIT_FILTER_STATE = {
    [CutFilterOptions.CutType]: {},
    [CutFilterOptions.Product]: {},
    [CutFilterOptions.Country]: {},
    [CutFilterOptions.Company]: {},
    [CutFilterOptions.Reseller]: {},
    [CutFilterOptions.Location]: {},
    [CutFilterOptions.Status]: {},
    [CutFilterOptions.Category]: {},
    [CutFilterOptions.Station]: {},
};

// Maps DataTable field names to backend SortField values
const COLUMN_TO_SORT_FIELD: Record<string, SortField> = {
    cutDate: SortField.CutDate,
    syncDate: SortField.SyncDate,
    cutType: SortField.CutType,
    cutStatus: SortField.Status,
    product: SortField.Product,
    company: SortField.Company,
    location: SortField.Location,
    manufacturer: SortField.Manufacturer,
    model: SortField.Model,
    coverage: SortField.Coverage,
    country: SortField.Country,
    employeeEmail: SortField.EmployeeEmail,
    category: SortField.Category,
};

const SORTABLE_FIELDS = new Set(Object.keys(COLUMN_TO_SORT_FIELD));

// Cut Type color mapping for StatusBadge
const CUT_TYPE_STATUS_MAP: Record<string, { label: string; status: 'success' | 'warning' | 'info' | 'error' }> = {
    [CutType.Successful]: { label: 'Successful Cut', status: 'success' },
    [CutType.Warranty]: { label: 'Warranty Claim', status: 'warning' },
    [CutType.FailedCut]: { label: 'Failed Cut', status: 'error' },
    [CutType.DiagnosticCut]: { label: 'Diagnostic Cut', status: 'info' },
};

export const CutActivity = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const {
        IdToken,
        Roles: { role },
    } = useSelector((state: RootState) => state.auth);
    const tz = useSelector((state: RootState) => state.timezone.tz);
    const { companies, resellers, locations, companyResellerMap, companyLocationMap, resellerLocationMap } = useOrgs();
    const { setAlert } = useAlert();

    const [searchParams, setSearchParams] = useSearchParams();

    // Filter panel state
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Columns menu state
    const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
    const columnsButtonRef = useRef<HTMLButtonElement>(null);
    const { scrollContainerRef: mobileScrollContainerRef, scrollToTop } = useScrollablePagination();

    const handlePageChange = useCallback(
        (newPage: number) => {
            setPageNumber(newPage);
            scrollToTop();
        },
        [scrollToTop]
    );

    const [searchInfo, setSearchInfo] = useMemo(() => {
        const search = searchParams.get('search');
        const type = searchParams.get('type');

        const info = {
            id: search || '',
            type:
                type && Object.values(SearchIDType).includes(type as any) ? (type as SearchIDType) : SearchIDType.CutID,
        };

        const setInfo = ({ id: search, type }: typeof info) => {
            setSearchParams(new URLSearchParams({ search, type }));
        };

        return [info, setInfo] as const;
    }, [searchParams, setSearchParams]);

    const [searchError, setSearchError] = useState('');

    const { firstDay } = getMonthInfo(1);
    const defaultRange = { startDate: dateFormat(firstDay), endDate: dateFormat() };
    const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(defaultRange);

    const [lastUpdated, setLastUpdated] = useState<string>('');
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [numberRecords, setNumberRecords] = useState<number>(0);

    const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.DESC);
    const [sortField, setSortField] = useState<SortField>(SortField.CutDate);
    const [entryCode, setEntryCode] = useState<string>('');
    const [employeeEmail, setEmployeeEmail] = useState<string>('');

    // Parse URL params once on mount to get initial filter state
    const initialUrlParams = useMemo(() => {
        const { filters: parsedFilters, paramsToDelete, stationLabel } = parseActivityParams(searchParams);
        const hasUrlFilters = Object.keys(parsedFilters).length > 0;

        return {
            hasUrlFilters,
            filters: hasUrlFilters ? { ...INIT_FILTER_STATE, ...parsedFilters } : INIT_FILTER_STATE,
            paramsToDelete,
            stationLabel,
            stationId: parsedFilters.stationIds ? Object.keys(parsedFilters.stationIds)[0] : null,
        };
    }, []); // Empty deps - only compute once on mount

    // Initialize filter state with URL params (or defaults)
    const [filters, setFilters] = useState<CutFilter>(initialUrlParams.filters);

    // Draft filter state for the panel
    const [draftDateRange, setDraftDateRange] = useState(defaultRange);
    const [draftSearchId, setDraftSearchId] = useState('');
    const [draftSearchType, setDraftSearchType] = useState<SearchIDType>(SearchIDType.CutID);
    const [draftFilters, setDraftFilters] = useState<CutFilter>(initialUrlParams.filters);
    const [draftEmployeeEmail, setDraftEmployeeEmail] = useState('');

    // Selected stations for the filter (stores label + id for display)
    const [selectedStations, setSelectedStations] = useState<{ stationId: string; label: string }[]>(() => {
        if (initialUrlParams.stationId && initialUrlParams.stationLabel) {
            return [{ stationId: initialUrlParams.stationId, label: initialUrlParams.stationLabel }];
        }
        return [];
    });

    // Clean up URL params after initial render (purely cosmetic - state is already initialized)
    useEffect(() => {
        if (initialUrlParams.hasUrlFilters) {
            const newParams = new URLSearchParams(searchParams);
            initialUrlParams.paramsToDelete.forEach((param) => newParams.delete(param));
            setSearchParams(newParams, { replace: true });
        }
    }, []);

    // Download models data
    const { data: modelsData } = useFetch({
        defaultValue: { count: 0, csv: '' },
        async fetcher() {
            const res = await fetch(new URL('data/models.json', UPDATES_URL));
            return (await res.json()) as { count: number; csv: string };
        },
        modifier(data) {
            return {
                ...data,
                csv: `data:text/csv;base64,${btoa(unescape(encodeURIComponent(data.csv)))}`,
            };
        },
        errorHandler() {
            console.error('Failed to retrieve list of models.');
        },
    });

    // Countries data for filter
    const { data: countriesData } = useFetch({
        fetcher: () => fetchOrgGeneric<{ countryName: string }[]>(OrgUrls.Countries)(IdToken!),
        defaultValue: [] as string[],
        modifier: (countries) => countries.map(({ countryName }) => countryName).sort(),
    });

    // Product names data for filter (fetched dynamically from API)
    const { data: productData } = useFetch<ProductName[], void>({
        fetcher: () => {
            if (!IdToken) return undefined;
            return fetchReport(REPORT.PRODUCTS_PRODUCT_NAMES, IdToken, {}).then((resp) => resp ?? []);
        },
        defaultValue: [] as ProductName[],
    });

    const productOptions = useMemo(
        () => productData.map(({ productName }) => ({ productName })),
        [productData]
    );

    // Scoped company options (all companies)
    const companyOptions = useMemo(() => Object.entries(companies).map(([id, name]) => ({ id, name })), [companies]);

    // Scoped reseller options - only show resellers under selected companies
    const resellerOptions = useMemo(() => {
        // Get only actual resellers (exclude companies from the reseller list)
        const actualResellers = differenceObjects(companies, resellers);
        const options = Object.entries(actualResellers).map(([id, name]) => ({ id, name }));

        return cascadeOptions({
            options,
            parent: draftFilters.companyIds,
            map: companyResellerMap,
            idKey: 'id',
        });
    }, [resellers, companies, draftFilters.companyIds, companyResellerMap]);

    // Scoped location options - cascade based on selected resellers or companies
    const locationOptions = useMemo(() => {
        const options = Object.entries(locations).map(([id, name]) => ({ id, name }));

        const byReseller = isFilterSelected(draftFilters, CutFilterOptions.Reseller);

        return cascadeOptions({
            options,
            parent: byReseller ? draftFilters.resellerIds : draftFilters.companyIds,
            map: byReseller ? resellerLocationMap : companyLocationMap,
            idKey: 'id',
        });
    }, [locations, draftFilters.resellerIds, draftFilters.companyIds, resellerLocationMap, companyLocationMap]);

    const setFilter = (key: CutFilterOptions, value: string, selectOne?: boolean) => {
        const isActive = filters[key][value];
        setFilters({ ...filters, [key]: { ...(selectOne ? {} : filters[key]), [value]: !isActive } });
    };

    // Remove a filter and immediately refetch (for chip removal)
    const removeFilterAndApply = (key: CutFilterOptions, value: string) => {
        const newFilters = {
            ...filters,
            [key]: { ...filters[key], [value]: false },
        };
        setFilters(newFilters);
        setPageNumber(0);
        refetch({
            uri: CutAPI.Activity,
            params: buildActivityParams({
                dateRange,
                filters: newFilters,
                searchId: searchInfo.id,
                searchType: searchInfo.type,
                entryCode,
                employeeEmail,
                sortDirection,
                sortField,
                pageNum: 0,
            }),
        });
    };

    // Remove date filter and immediately refetch
    const removeDateFilterAndApply = () => {
        setDateRange(defaultRange);
        setPageNumber(0);
        refetch({
            uri: CutAPI.Activity,
            params: buildActivityParams({
                dateRange: defaultRange,
                filters,
                searchId: searchInfo.id,
                searchType: searchInfo.type,
                entryCode,
                employeeEmail,
                sortDirection,
                sortField,
                pageNum: 0,
            }),
        });
    };

    const uri = CutAPI.Activity;

    const params = buildActivityParams({
        dateRange,
        filters,
        searchId: searchInfo.id,
        searchType: searchInfo.type,
        entryCode,
        employeeEmail,
        sortDirection,
        sortField,
        pageNum: pageNumber,
    });

    const { data, loading, refetch, setData } = useFetch({
        spinner: false,
        fetcher: (input?: { uri: CutAPI; params: Record<string, any> }) => {
            return cutApi(input?.uri || uri, 'GET')({
                token: IdToken!,
                tz,
                params: input?.params || params,
            }) as Promise<Cut[]>;
        },
        defaultValue: [] as Cut[],
        errorHandler: () => {
            setData([]);
            setAlert('No results - an error occurred.', 'error');
        },
    });

    useEffect(() => {
        refetch();
    }, [sortDirection, sortField, pageNumber, tz]);

    useEffect(() => {
        if (!searchParams.has('auto')) return;

        refetch();
        setSearchParams((prev) => {
            prev.delete('auto');
            return prev;
        });
    }, [searchParams]);

    useEffect(() => {
        const [cut] = data;
        const recordCount = cut ? +cut.totalCutsInQuery : 0;
        setNumberRecords(recordCount);
        setLastUpdated(dayjs().format('hh:mm:ssa - MM/DD/YYYY'));
    }, [data]);

    // Product filtering is applied server-side (productNames param), so the
    // fetched page is already scoped — no client-side post-filter that would
    // otherwise truncate paginated results.
    const mappedData = useMemo(
        () => transformCuts(data, companies, locations, resellers),
        [data, companies, locations, resellers]
    );

    const dateRangeError = useMemo(
        () => dayjs(dateRange.startDate).add(DATE_LIMIT, 'month').diff(dayjs(dateRange.endDate)) < 0,
        [dateRange]
    );

    // Column visibility preferences, persisted in localStorage
    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        value: DEFAULT_COLUMN_FILTER_STATE,
        key: 'cut-activity-column-visibility',
    });

    // Column order preferences, persisted in localStorage
    const [columnOrder, setColumnOrder] = useLocalStorage({
        value: DEFAULT_COLUMN_ORDER,
        key: 'cut-activity-column-order',
    });

    // Convert old column format to design system DataTable format, respecting saved order
    const designColumns = useMemo<DataTableColumn<ParsedCut>[]>(() => {
        // Sort columns by saved order, then filter by visibility
        const orderedColumns = [...CUT_ACTIVITY_COLUMNS].sort((a, b) => {
            const aIndex = columnOrder.indexOf(a.field as string);
            const bIndex = columnOrder.indexOf(b.field as string);
            return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
        });

        return orderedColumns
            .filter(({ field }) => columnVisibility[field])
            .map((col) => {
                const baseColumn: DataTableColumn<ParsedCut> = {
                    field: col.field as keyof ParsedCut,
                    headerName: col.headerName,
                    minWidth: col.minWidth,
                    width: col.width,
                    flex: col.flex,
                    sortable: SORTABLE_FIELDS.has(col.field),
                };

                // Handle special rendering for cut type with StatusBadge
                if (col.field === 'cutType') {
                    baseColumn.renderCell = ({ value }) => {
                        const config = CUT_TYPE_STATUS_MAP[value as string];
                        if (config) {
                            return <StatusBadge label={config.label} status={config.status} size='small' />;
                        }
                        return value;
                    };
                }

                // Handle date fields with links — backend returns ISO UTC, format in user's selected TZ
                if (col.field === 'cutDate' || col.field === 'syncDate') {
                    baseColumn.renderCell = ({ row, value }) => {
                        const display = value ? dayjs.utc(value as string).tz(tz).format('YYYY-MM-DD HH:mm:ss') : value;
                        return <TableLink to={`${AppRoutes.CutHistory}?id=${row.dbCutId}`}>{display}</TableLink>;
                    };
                }

                // Handle cutId with link
                if (col.field === 'cutId') {
                    baseColumn.renderCell = ({ row, value }) => (
                        <TableLink to={`${AppRoutes.CutHistory}?id=${row.dbCutId}`}>{value}</TableLink>
                    );
                }

                // Handle batchId with link
                if (col.field === 'batchId') {
                    baseColumn.renderCell = ({ row, value }) =>
                        value ? (
                            <TableLink to={`${AppRoutes.Activity}?search=${value}&type=BatchID&auto=`}>
                                {value}
                            </TableLink>
                        ) : (
                            '-'
                        );
                }

                // Handle invoiceId with link
                if (col.field === 'invoiceId') {
                    baseColumn.renderCell = ({ row, value }) =>
                        value ? (
                            <TableLink to={`${AppRoutes.Activity}?search=${value}&type=InvoiceID&auto=`}>
                                {value}
                            </TableLink>
                        ) : (
                            '-'
                        );
                }

                // Handle creditId with link
                if (col.field === 'creditId') {
                    baseColumn.renderCell = ({ row, value }) =>
                        value ? (
                            <TableLink to={`${AppRoutes.Activity}?search=${value}&type=CreditID&auto=`}>
                                {value}
                            </TableLink>
                        ) : (
                            '-'
                        );
                }

                // Handle company with link to manage
                if (col.field === 'company') {
                    baseColumn.renderCell = ({ row, value }) => (
                        <TableLink to={`${AppRoutes.Organizations}?type=company&id=${row.companyIdRaw}`}>
                            {value || '-'}
                        </TableLink>
                    );
                }

                // Handle reseller with link to manage
                if (col.field === 'reseller') {
                    baseColumn.renderCell = ({ row, value }) => (
                        <TableLink to={`${AppRoutes.Organizations}?type=reseller&id=${row.resellerIdRaw}`}>
                            {value || '-'}
                        </TableLink>
                    );
                }

                // Handle locationId with link to manage
                if (col.field === 'locationId') {
                    baseColumn.renderCell = ({ row, value }) => (
                        <TableLink to={`${AppRoutes.Organizations}?type=location&id=${row.locationIdRaw}`}>
                            {value || '-'}
                        </TableLink>
                    );
                }

                // Handle location with link to manage
                if (col.field === 'location') {
                    baseColumn.renderCell = ({ row, value }) => (
                        <TableLink to={`${AppRoutes.Organizations}?type=location&id=${row.locationIdRaw}`}>
                            {value || '-'}
                        </TableLink>
                    );
                }

                // Handle stationId with link to manage
                if (col.field === 'stationId') {
                    baseColumn.renderCell = ({ row, value }) =>
                        row.stationIdRaw ? (
                            <TableLink to={`${AppRoutes.Organizations}?type=system&id=${row.stationIdRaw}`}>
                                {value}
                            </TableLink>
                        ) : (
                            '-'
                        );
                }

                // Handle serialNum with link to manage
                if (col.field === 'serialNum') {
                    baseColumn.renderCell = ({ row, value }) =>
                        row.stationIdRaw && value ? (
                            <TableLink to={`${AppRoutes.Organizations}?type=system&id=${row.stationIdRaw}`}>
                                {value}
                            </TableLink>
                        ) : (
                            value || '-'
                        );
                }

                return baseColumn;
            });
    }, [columnVisibility, columnOrder, tz]);

    const handleFilterClear = () => {
        setSortDirection(SortDirections.DESC);
        setSortField(SortField.CutDate);
        setPageNumber(0);
        setDateRange(defaultRange);
        setFilters({ ...INIT_FILTER_STATE });
        setEntryCode('');
        setEmployeeEmail('');
        setSearchInfo({
            id: '',
            type: SearchIDType.CutID,
        });
        setSearchError('');
        setColumnVisibility(DEFAULT_COLUMN_FILTER_STATE);
        setColumnOrder(DEFAULT_COLUMN_ORDER);
        setDraftDateRange(defaultRange);
        setDraftFilters({ ...INIT_FILTER_STATE });
        setDraftSearchId('');
        setDraftSearchType(SearchIDType.CutID);
        setDraftEmployeeEmail('');
        setSelectedStations([]);

        refetch({
            uri: CutAPI.Activity,
            params: buildActivityParams({
                dateRange: defaultRange,
                filters: INIT_FILTER_STATE,
                searchId: '',
                searchType: SearchIDType.CutID,
                entryCode: '',
                employeeEmail: '',
                sortDirection: SortDirections.DESC,
                sortField: SortField.CutDate,
                pageNum: 0,
            }),
        });
    };

    const handleExport = () => {
        const columns = Object.entries(columnVisibility)
            .filter(([, visible]) => visible)
            .map(([field]) => field);

        openPopup(`${AppRoutes.ExportActivity}?params=${JSON.stringify(params)}&columns=${JSON.stringify(columns)}`, {
            width: 400,
            height: 400,
        });
    };

    const handleApplyFilters = () => {
        setDateRange(draftDateRange);
        setFilters(draftFilters);
        setEmployeeEmail(draftEmployeeEmail);
        setSearchInfo({ id: draftSearchId, type: draftSearchType });
        setFiltersOpen(false);

        if (pageNumber === 0) {
            refetch({
                uri: CutAPI.Activity,
                params: buildActivityParams({
                    dateRange: draftDateRange,
                    filters: draftFilters,
                    searchId: draftSearchId,
                    searchType: draftSearchType,
                    entryCode,
                    employeeEmail: draftEmployeeEmail,
                    sortDirection,
                    sortField,
                    pageNum: 0,
                }),
            });
        } else {
            setPageNumber(0);
        }
    };

    const handleResetFilters = () => {
        setDraftDateRange(defaultRange);
        setDraftFilters({ ...INIT_FILTER_STATE });
        setDraftSearchId('');
        setDraftSearchType(SearchIDType.CutID);
        setDraftEmployeeEmail('');
        setSelectedStations([]);
    };

    const handleOpenFilters = () => {
        setDraftDateRange(dateRange);
        setDraftFilters(filters);
        setDraftSearchId(searchInfo.id);
        setDraftSearchType(searchInfo.type);
        setDraftEmployeeEmail(employeeEmail);
        setFiltersOpen(true);
    };

    const handleColumnToggle = (fieldName: string) => {
        setColumnVisibility((prev) => {
            const count = Object.values(prev).filter(Boolean).length;
            const selected = prev[fieldName];

            // Prevent hiding the last column
            if (selected && count <= 1) {
                return prev;
            }

            return {
                ...prev,
                [fieldName]: !selected,
            };
        });
    };

    const handleSortModelChange = useCallback(
        (model: GridSortModel) => {
            if (model.length === 0) {
                setSortField(SortField.CutDate);
                setSortDirection(SortDirections.DESC);
            } else {
                const { field, sort } = model[0];
                const mapped = COLUMN_TO_SORT_FIELD[field];
                if (mapped) {
                    setSortField(mapped);
                    setSortDirection(sort === 'asc' ? SortDirections.ASC : SortDirections.DESC);
                }
            }
            setPageNumber(0);
        },
        []
    );

    const sortModel = useMemo<GridSortModel>(() => {
        const columnField = Object.entries(COLUMN_TO_SORT_FIELD).find(
            ([, value]) => value === sortField
        )?.[0];
        if (!columnField) return [];
        return [{ field: columnField, sort: sortDirection === SortDirections.ASC ? 'asc' : 'desc' }];
    }, [sortField, sortDirection]);

    // Count active filters
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (dateRange.startDate !== defaultRange.startDate || dateRange.endDate !== defaultRange.endDate) {
            count += 1;
        }
        Object.values(filters).forEach((filterGroup) => {
            if (Object.values(filterGroup).some(Boolean)) {
                count += 1;
            }
        });
        if (employeeEmail) count += 1;
        return count;
    }, [dateRange, filters, defaultRange, employeeEmail]);

    // Build filter chips for display above table
    const filterChips = useMemo(() => {
        const chips: React.ReactNode[] = [];

        if (dateRange.startDate !== defaultRange.startDate || dateRange.endDate !== defaultRange.endDate) {
            const startFormatted = dayjs(dateRange.startDate).format('MMM D');
            const endFormatted = dayjs(dateRange.endDate).format('MMM D, YYYY');
            chips.push(
                <FilterChip
                    key='date-range'
                    label={`${startFormatted} – ${endFormatted}`}
                    onRemove={() => removeDateFilterAndApply()}
                />
            );
        }

        // Category chips
        Object.entries(filters.category)
            .filter(([, active]) => active)
            .forEach(([category]) => {
                chips.push(
                    <FilterChip
                        key={`category-${category}`}
                        label={category}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Category, category)}
                    />
                );
            });

        // Cut type chips
        Object.entries(filters.type)
            .filter(([, active]) => active)
            .forEach(([type]) => {
                chips.push(
                    <FilterChip
                        key={`type-${type}`}
                        label={CUT_TYPE_STATUS_MAP[type]?.label || type}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.CutType, type)}
                    />
                );
            });

        // Product name chips (dynamic from API)
        Object.entries(filters.productNames)
            .filter(([, active]) => active)
            .forEach(([productName]) => {
                chips.push(
                    <FilterChip
                        key={`product-${productName}`}
                        label={productName}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Product, productName)}
                    />
                );
            });

        // Country chips
        Object.entries(filters.countryName)
            .filter(([, active]) => active)
            .forEach(([country]) => {
                chips.push(
                    <FilterChip
                        key={`country-${country}`}
                        label={country}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Country, country)}
                    />
                );
            });

        // Company chips
        Object.entries(filters.companyIds)
            .filter(([, active]) => active)
            .forEach(([companyId]) => {
                const companyName = companies[companyId] || companyId;
                chips.push(
                    <FilterChip
                        key={`company-${companyId}`}
                        label={companyName}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Company, companyId)}
                    />
                );
            });

        // Reseller chips
        Object.entries(filters.resellerIds)
            .filter(([, active]) => active)
            .forEach(([resellerId]) => {
                const resellerName = resellers[resellerId] || resellerId;
                chips.push(
                    <FilterChip
                        key={`reseller-${resellerId}`}
                        label={resellerName}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Reseller, resellerId)}
                    />
                );
            });

        // Location chips
        Object.entries(filters.locationIds)
            .filter(([, active]) => active)
            .forEach(([locationId]) => {
                const locationName = locations[locationId] || locationId;
                chips.push(
                    <FilterChip
                        key={`location-${locationId}`}
                        label={`#${locationId} - ${locationName}`}
                        onRemove={() => removeFilterAndApply(CutFilterOptions.Location, locationId)}
                    />
                );
            });

        // Station chips
        Object.entries(filters.stationIds)
            .filter(([, active]) => active)
            .forEach(([stationId]) => {
                const station = selectedStations.find((s) => s.stationId === stationId);
                chips.push(
                    <FilterChip
                        key={`station-${stationId}`}
                        label={station?.label || `Station #${stationId}`}
                        onRemove={() => {
                            removeFilterAndApply(CutFilterOptions.Station, stationId);
                            setSelectedStations((prev) => prev.filter((s) => s.stationId !== stationId));
                        }}
                    />
                );
            });

        // Employee email chip
        if (employeeEmail) {
            chips.push(
                <FilterChip
                    key='employee-email'
                    label={`Email: ${employeeEmail}`}
                    onRemove={() => {
                        setEmployeeEmail('');
                        setPageNumber(0);
                        refetch({
                            uri: CutAPI.Activity,
                            params: {
                                ...dateRange,
                                ...getFilterParams(filters),
                                entryCode: entryCode || undefined,
                                sortDirection,
                                sortField,
                                pageNum: 0,
                                pageSize: PAGE_SIZE,
                            },
                        });
                    }}
                />
            );
        }

        return chips;
    }, [dateRange, filters, defaultRange, companies, resellers, locations, selectedStations, employeeEmail]);

    const activeColumnCount = Object.values(columnVisibility).filter(Boolean).length;

    // Pagination info text
    const startItem = pageNumber * PAGE_SIZE + 1;
    const endItem = Math.min((pageNumber + 1) * PAGE_SIZE, numberRecords);
    const infoText = numberRecords > 0 ? `${startItem}–${endItem} of ${numberRecords}` : 'No cuts found';

    return (
        <div className={classes.root}>
            <ScrollToTop />

            {/* SubBar with Breadcrumbs - Desktop only */}
            {!isMobile && <BreadcrumbSubBar />}

            <Box
                sx={{
                    backgroundColor: colors.neutral[100],
                    px: isMobile ? 0 : 3,
                    py: isMobile ? 0 : 3,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}>
                {/* Page Header - Desktop only */}
                {!isMobile && (
                    <ContentPageHeader
                        title='Cut Activity'
                        description={
                            <>
                                Below is the cut activity across all of your RapidCut Systems. If you are looking for
                                invoices related to your cut activity, all invoices can be found in{' '}
                                <Link href='https://account.moutfitters.com/' target='_blank'>
                                    Account Central
                                </Link>
                                .
                            </>
                        }
                    />
                )}

                {/* Active Filter Chips (if any) */}
                {filterChips.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: isMobile ? 'nowrap' : 'wrap',
                            overflowX: isMobile ? 'auto' : 'visible',
                            px: isMobile ? 2 : 0,
                            py: isMobile ? 1.5 : 0,
                            mb: isMobile ? 0 : 2,
                            backgroundColor: isMobile ? colors.neutral[100] : 'transparent',
                            borderBottom: isMobile ? `1px solid ${colors.neutral[200]}` : 'none',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': {
                                display: 'none',
                            },
                            scrollbarWidth: 'none',
                        }}>
                        {filterChips}
                        <Typography
                            component='button'
                            onClick={handleFilterClear}
                            sx={{
                                background: 'none',
                                border: 'none',
                                color: colors.neutral[600],
                                fontSize: '0.8125rem',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                flexShrink: 0,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}>
                            Clear all
                        </Typography>
                    </Box>
                )}

                {/* Mobile View - Outside TableCard for full width */}
                {isMobile ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}>
                        {/* Row 1: Title + Filter Button */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                pt: 2,
                                pb: 1.5,
                                backgroundColor: colors.neutral[0],
                                borderBottom: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: '1.125rem',
                                    fontWeight: 700,
                                    color: colors.neutral[900],
                                }}>
                                Cut Activity
                            </Typography>
                            <Button
                                variant='secondary'
                                size='small'
                                onClick={handleOpenFilters}
                                startIcon={<FilterListIcon />}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1,
                                    '& .MuiButton-startIcon': {
                                        margin: 0,
                                    },
                                }}>
                                {''}
                            </Button>
                        </Box>

                        {/* Row 2: Info text + Export Button */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                py: 1.5,
                                backgroundColor: colors.neutral[0],
                                borderBottom: `1px solid ${colors.neutral[200]}`,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: colors.neutral[600],
                                }}>
                                {numberRecords > 0 ? infoText : 'No cuts found'}
                            </Typography>
                            <Button
                                variant='secondary'
                                size='small'
                                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                                onClick={handleExport}>
                                Export
                            </Button>
                        </Box>

                        {/* Cards Container */}
                        <Box
                            ref={mobileScrollContainerRef}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                p: 1.5,
                                backgroundColor: colors.neutral[50],
                            }}>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, i) => <MobileCutCardSkeleton key={i} />)
                            ) : mappedData.length === 0 ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: colors.neutral[500],
                                    }}>
                                    <Typography>No cuts found</Typography>
                                </Box>
                            ) : (
                                mappedData.map((cut) => (
                                    <MobileCutCard key={cut.dbCutId} data={cut} />
                                ))
                            )}
                        </Box>

                        {/* Pagination - Fixed at bottom */}
                        {numberRecords > PAGE_SIZE && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 1.5,
                                    backgroundColor: colors.neutral[0],
                                    borderTop: `1px solid ${colors.neutral[200]}`,
                                }}>
                                <Pagination
                                    count={Math.ceil(numberRecords / PAGE_SIZE)}
                                    page={pageNumber + 1}
                                    onChange={(_, page) => handlePageChange(page - 1)}
                                    color='primary'
                                    size='small'
                                    siblingCount={0}
                                    boundaryCount={1}
                                    sx={{
                                        '& .MuiPaginationItem-root': {
                                            borderRadius: '8px',
                                            minWidth: 32,
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                ) : (
                    /* Desktop View - Inside TableCard */
                    <TableCard
                        title=''
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}>
                        <TableInfoHeader
                            infoText={
                                <Typography
                                    sx={{
                                        fontSize: '0.875rem',
                                        color: colors.neutral[700],
                                    }}>
                                    Total: <strong>{numberRecords}</strong> cuts
                                </Typography>
                            }
                            actions={
                                <>
                                    {/* Filters Button */}
                                    <Button
                                        variant={filtersOpen ? 'primary' : 'secondary'}
                                        size='small'
                                        onClick={handleOpenFilters}
                                        startIcon={<FilterListIcon />}>
                                        Filters
                                    </Button>

                                    {/* Columns Button */}
                                    <Button
                                        ref={columnsButtonRef}
                                        variant='secondary'
                                        size='small'
                                        onClick={() => setColumnsMenuOpen(true)}
                                        startIcon={<ViewColumnOutlinedIcon />}>
                                        Columns ({activeColumnCount})
                                    </Button>
                                    <Menu
                                        anchorEl={columnsButtonRef.current}
                                        open={columnsMenuOpen}
                                        onClose={() => setColumnsMenuOpen(false)}
                                        PaperProps={{
                                            sx: {
                                                maxHeight: 400,
                                                minWidth: 200,
                                            },
                                        }}>
                                        {columnOrder.map((field) => {
                                            const col = CUT_ACTIVITY_COLUMNS.find((c) => c.field === field);
                                            if (!col) return null;
                                            return (
                                                <MenuItem
                                                    key={col.field}
                                                    onClick={() => handleColumnToggle(col.field)}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        gap: 2,
                                                    }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            color: columnVisibility[col.field]
                                                                ? colors.neutral[900]
                                                                : colors.neutral[500],
                                                        }}>
                                                        {col.headerName}
                                                    </Typography>
                                                    {columnVisibility[col.field] && (
                                                        <CheckIcon sx={{ fontSize: 18, color: colors.primary[500] }} />
                                                    )}
                                                </MenuItem>
                                            );
                                        })}
                                    </Menu>

                                    {/* Export Button */}
                                    {getDeviceType(navigator.userAgent) === 'desktop' && (
                                        <Button
                                            variant='secondary'
                                            size='small'
                                            onClick={handleExport}
                                            startIcon={<FileDownloadOutlinedIcon />}>
                                            Export
                                        </Button>
                                    )}

                                    {/* Device Catalogue Export */}
                                    {modelsData.csv && (
                                        <a
                                            href={modelsData.csv}
                                            download='device-catalogue.csv'
                                            style={{ textDecoration: 'none' }}>
                                            <Button
                                                variant='secondary'
                                                size='small'
                                                startIcon={<CloudDownloadIcon />}>
                                                Device Catalogue ({modelsData.count})
                                            </Button>
                                        </a>
                                    )}
                                </>
                            }
                        />

                        {/* Desktop View: Data Table */}
                        <DesignDataTable
                            rows={mappedData}
                            columns={designColumns}
                            getRowId={(row) => row.dbCutId}
                            totalRows={numberRecords}
                            page={pageNumber + 1}
                            pageSize={PAGE_SIZE}
                            onPageChange={(page) => setPageNumber(page - 1)}
                            onColumnOrderChange={setColumnOrder}
                            loading={loading}
                            infoText={infoText}
                            height='100%'
                            sortingMode='server'
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                        />
                    </TableCard>
                )}

                {/* Cut Date Upload - Commented out
                <Box display={'flex'} mt={2}>
                    <CutDateUpload />
                </Box>
                */}
            </Box>

            {/* Filter Panel */}
            <FilterPanel
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}>
                {/* Date Range Section */}
                <FilterSection title='Date Range'>
                    <CustomDatePicker dateRange={draftDateRange} setDateRange={setDraftDateRange} />
                </FilterSection>

                {/* Cut Details Section */}
                <FilterSection title='Cut Details'>
                    <SelectField
                        label='Search Type'
                        value={draftSearchType}
                        onChange={(value) => setDraftSearchType(value as SearchIDType)}
                        options={[
                            { value: SearchIDType.CutID, label: 'Cut ID' },
                            { value: SearchIDType.BatchID, label: 'Batch ID' },
                            { value: SearchIDType.CreditID, label: 'Credit ID' },
                            { value: SearchIDType.InvoiceID, label: 'Invoice ID' },
                        ]}
                    />
                    <TextField
                        label={TYPES[draftSearchType].label}
                        value={draftSearchId}
                        onChange={setDraftSearchId}
                        placeholder={`Ex: ${TYPES[draftSearchType].example}`}
                    />
                    <SelectField
                        label='Cut Type'
                        value={Object.keys(draftFilters.type).find((k) => draftFilters.type[k]) || ''}
                        onChange={(value) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                type: value ? { [value as string]: true } : {},
                                // Reset status when cut type changes
                                status: {},
                            }));
                        }}
                        options={[
                            { value: '', label: 'All Types' },
                            { value: CutType.Successful, label: 'Successful Cut' },
                            { value: CutType.Warranty, label: 'Warranty Claim' },
                            { value: CutType.FailedCut, label: 'Failed Cut' },
                            { value: CutType.DiagnosticCut, label: 'Diagnostic Cut' },
                        ]}
                    />
                </FilterSection>

                {/* Product Section */}
                <FilterSection title='Product'>
                    <SelectField
                        label='Category'
                        value={Object.keys(draftFilters.category).find((k) => draftFilters.category[k]) || ''}
                        onChange={(value) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                category: value ? { [value as string]: true } : {},
                            }));
                        }}
                        options={[
                            { value: '', label: 'All Categories' },
                            { value: 'Smartphones', label: 'Smartphones' },
                            { value: 'Smartwatches', label: 'Smartwatches' },
                            { value: 'Tablets', label: 'Tablets' },
                            { value: 'Other', label: 'Other' },
                        ]}
                    />
                    <MultiSelectFilter
                        label='Product'
                        options={productOptions}
                        value={draftFilters[CutFilterOptions.Product]}
                        onChange={(value: { productName: string }) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                [CutFilterOptions.Product]: {
                                    ...prev[CutFilterOptions.Product],
                                    [value.productName]: !prev[CutFilterOptions.Product][value.productName],
                                },
                            }));
                        }}
                        idKey='productName'
                        getLabelValue={(option) => option.productName}
                        margin={false}
                    />
                </FilterSection>

                {/* Organization Section */}
                <FilterSection title='Organization'>
                    <MultiSelectFilter
                        label='Country'
                        options={countriesData}
                        value={draftFilters.countryName}
                        onChange={(country) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                countryName: {
                                    ...prev.countryName,
                                    [country as string]: !prev.countryName[country as string],
                                },
                            }));
                        }}
                        getLabelValue={(country) => country}
                        search
                        margin={false}
                    />
                    <MultiSelectFilter
                        label='Company'
                        options={companyOptions}
                        value={draftFilters.companyIds}
                        onChange={(company) => {
                            const companyObj = company as { id: string; name: string };
                            setDraftFilters((prev) => ({
                                ...prev,
                                companyIds: {
                                    ...prev.companyIds,
                                    [companyObj.id]: !prev.companyIds[companyObj.id],
                                },
                            }));
                        }}
                        getLabelValue={(company) => company.name}
                        idKey='id'
                        search
                        margin={false}
                    />
                    <MultiSelectFilter
                        label='Reseller'
                        options={resellerOptions}
                        value={draftFilters.resellerIds}
                        onChange={(reseller) => {
                            const resellerObj = reseller as { id: string; name: string };
                            setDraftFilters((prev) => ({
                                ...prev,
                                resellerIds: {
                                    ...prev.resellerIds,
                                    [resellerObj.id]: !prev.resellerIds[resellerObj.id],
                                },
                            }));
                        }}
                        getLabelValue={(reseller) => reseller.name}
                        idKey='id'
                        search
                        margin={false}
                    />
                    <MultiSelectFilter
                        label='Location'
                        options={locationOptions}
                        value={draftFilters.locationIds}
                        onChange={(location) => {
                            const locationObj = location as { id: string; name: string };
                            setDraftFilters((prev) => ({
                                ...prev,
                                locationIds: {
                                    ...prev.locationIds,
                                    [locationObj.id]: !prev.locationIds[locationObj.id],
                                },
                            }));
                        }}
                        getLabelValue={(location) => `#${location.id} - ${location.name}`}
                        idKey='id'
                        search
                        margin={false}
                    />
                    <StationSearchFilter
                        label='System'
                        value={draftFilters.stationIds}
                        selectedStations={selectedStations}
                        onChange={(station) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                stationIds: {
                                    ...prev.stationIds,
                                    [station.stationId]: !prev.stationIds[station.stationId],
                                },
                            }));
                            setSelectedStations((prev) => {
                                const exists = prev.some((s) => s.stationId === station.stationId);
                                if (exists) {
                                    return prev.filter((s) => s.stationId !== station.stationId);
                                }
                                return [...prev, station];
                            });
                        }}
                        onRemove={(stationId) => {
                            setDraftFilters((prev) => ({
                                ...prev,
                                stationIds: {
                                    ...prev.stationIds,
                                    [stationId]: false,
                                },
                            }));
                            setSelectedStations((prev) => prev.filter((s) => s.stationId !== stationId));
                        }}
                    />
                </FilterSection>

                {/* Employee Section */}
                <FilterSection title='Employee'>
                    <TextField
                        label='Employee Email'
                        value={draftEmployeeEmail}
                        onChange={setDraftEmployeeEmail}
                        placeholder='e.g. john@example.com'
                    />
                </FilterSection>
            </FilterPanel>
        </div>
    );
};
